import { supabase } from '../lib/supabase';
import { booksChanged } from '../lib/changed';
import { todayIn } from '../book/model';

// Add & import (My books Part 4): your imports, and Import a list's two database steps (0048).

type Row = Record<string, unknown>;

export interface PastImport {
  id: string;
  name: string;
  kind: string;
  on: string;
  /** How many books it brought: the sheet import's count, or a typed list's added. */
  books: number;
  counts: Record<string, number>;
}

/** Your imports, newest first. An undone import is gone (Kristi, Oct 31: as Undo is everywhere). */
export async function loadPastImports(): Promise<PastImport[]> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const { data, error } = await db.from('import_batches').select('id, name, kind, counts, created_at').eq('state', 'done').order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data as Row[] | null) ?? []).map((b) => {
    const counts = (b.counts as Record<string, number> | null) ?? {};
    return {
      id: b.id as string,
      name: (b.name as string) ?? 'Import',
      kind: (b.kind as string) ?? 'other',
      // The Pacific day it ran (consistency pass, R8 W3), not the UTC date.
      on: b.created_at ? todayIn(undefined, new Date(b.created_at as string)) : '',
      books: Number(counts.added ?? counts.books ?? 0),
      counts,
    };
  });
}

export interface ImportResult {
  batch: string;
  counts: Record<string, number>;
  added: { seq: number; book: string; label: string }[];
  failed: { seq: number; message: string }[];
}

export async function runImport(name: string, items: unknown[]): Promise<ImportResult> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.rpc('import_list', { name, items });
  if (error) throw new Error(error.message);
  booksChanged();
  return data as ImportResult;
}

export interface UndoResult {
  removed: number;
  /** Copies the import added to books you had (0049). */
  copies_removed?: number;
  kept: { book: string; copy?: string; label: string; title: string }[];
}

export async function undoImport(batch: string): Promise<UndoResult> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.rpc('import_undo', { batch });
  if (error) throw new Error(error.message);
  booksChanged();
  return data as UndoResult;
}

/** Rename an import (Kristi, Oct 31): yours, so the owner rules let you; 1–200 characters. */
export async function renameImport(id: string, name: string): Promise<void> {
  if (!supabase) throw new Error('Not connected to a database.');
  const n = name.trim().slice(0, 200);
  if (!n) throw new Error('An import needs a name.');
  const { error } = await supabase.from('import_batches').update({ name: n }).eq('id', id);
  if (error) throw new Error(error.message);
  booksChanged();
}

/** The formats you have of these books, for an "on your list" row ("Kindle · Owned"). */
export async function loadHaveFormats(bookIds: string[]): Promise<Map<string, string[]>> {
  const out = new Map<string, string[]>();
  if (!supabase || !bookIds.length) return out;
  const { data, error } = await supabase.from('copies').select('book_id, format').in('book_id', bookIds).neq('how', 'library_loan').in('state', ['have_it', 'lent_out']);
  if (error) throw new Error(error.message);
  for (const c of (data as Row[] | null) ?? []) {
    const f = c.format as string | null;
    if (!f) continue;
    const name = f === 'pdf_epub' ? 'PDF / EPUB' : f.charAt(0).toUpperCase() + f.slice(1).replace(/_/g, ' ');
    const list = out.get(c.book_id as string) ?? [];
    if (!list.includes(name)) list.push(name);
    out.set(c.book_id as string, list);
  }
  return out;
}

/**
 * The lines of your earlier imports and the book each became (Kristi, Nov 1): pasting the same
 * line again finds that book on your list, however it was matched (a search you changed, a pick).
 * Keyed by the line's words, any case.
 */
export async function loadLineMemory(): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (!supabase) return out;
  const { data, error } = await supabase.from('import_items').select('line, book_id').not('book_id', 'is', null).is('undone_at', null).order('created_at', { ascending: false }).limit(2000);
  if (error) throw new Error(error.message);
  for (const r of (data as Row[] | null) ?? []) {
    const k = lineKey(r.line as string);
    if (k && !out.has(k)) out.set(k, r.book_id as string);
  }
  return out;
}

/** A line's words, any case, for the memory above. */
export const lineKey = (line: string) =>
  line
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Marks the paper copies an import just added as lost or donated (check-up, Kristi Nov 2, option a):
 * the newest paper copy you still have of each book, through copy_edit's state, as on Owned copies.
 * Returns how many were marked and the ones that weren't.
 */
export async function markImportedStates(items: { book: string; state: 'lost' | 'donated'; title: string }[]): Promise<{ marked: number; failed: string[] }> {
  if (!supabase || !items.length) return { marked: 0, failed: [] };
  const { data, error } = await supabase
    .from('copies')
    .select('id, book_id, created_at')
    .in('book_id', items.map((i) => i.book))
    .eq('format', 'paper')
    .eq('state', 'have_it')
    .neq('how', 'library_loan')
    .order('created_at', { ascending: false });
  if (error) return { marked: 0, failed: items.map((i) => `${i.title}: ${error.message}`) };
  const newest = new Map<string, string>();
  for (const c of (data ?? []) as { id: string; book_id: string }[]) if (!newest.has(c.book_id)) newest.set(c.book_id, c.id);
  let marked = 0;
  const failed: string[] = [];
  for (const i of items) {
    const copy = newest.get(i.book);
    if (!copy) {
      failed.push(`${i.title}: no paper copy found`);
      continue;
    }
    const { error: e } = await supabase.rpc('copy_edit', { copy, field: 'state', value: i.state });
    if (e) failed.push(`${i.title}: ${e.message}`);
    else marked++;
  }
  if (marked) booksChanged();
  return { marked, failed };
}
